import { useMemo, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { DateTime } from "luxon";
import theme from "../../../theme";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { NewMonitoringRecordFrame } from "./NewMonitoringRecordFrame";
import { Paragraph } from "../../../components/Paragraph";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { MonitoringEventsFields } from "../../../components/MonitoringEventsFields";
import { Loader } from "../../../components/Loader";
import { Input } from "../../../components/Input";
import Select, { Option } from "../../../components/Select";
import { DatePicker } from "../../../components/DatePicker";
import { Modal } from "../../../components/Modal";
import { useCountries } from "../../../data/useCountries";
import { useEntityTypes } from "../../../data/useEntityTypes";
import { useCommonTitles } from "../../../data/useCommonTitles";
import { usePatchProfile } from "../../../data/usePatchProfile";
import { useCreateComment } from "../../../data/useCreateComment";
import { useMembershipDivisionTeam } from "../../../hooks/useMembershipDivisionTeam";
import {
  monitoringEventsValidation,
  addIndividualValidationOptionalEmail,
  noHtmlTags,
} from "../../../utils/validationSchema";
import { MIN_AGE_APPLICATION } from "../../../utils/constants";
import { showError } from "../../../utils/error-handling";
import { notify } from "../../../utils/notify";
import { useCompanyStatusOptions } from "../../../data/fallbackPayloads/useCompanyStatusOptions";

const toJSDate = (value?: string | null) => {
  if (!value) return null;
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toJSDate() : null;
};

const normalize = (v: any) => {
  if (v === undefined || v === null) return "";
  if (v instanceof Date) return DateTime.fromJSDate(v).toISODate() ?? "";
  if (typeof v === "number") return String(v);
  return String(v).trim();
};

const hasChanges = (a: Record<string, any>, b: Record<string, any>) => {
  const keys = Object.keys(a);
  return keys.some((k) => normalize(a[k]) !== normalize(b[k]));
};

type DetailsValues = {
  externalReference: string;
  division: number | string;
  team: number | string;

  // entity
  name: string;
  country: number | null;
  entity_type: number | null;
  company_status: number | null;
  registration_number: string;
  date_of_incorporation: Date | null;

  // individual
  title: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: Date | null;
  gender: string;
};

type FormValues = DetailsValues & {
  pep_and_sanction_required: boolean | string;
  adverse_media_required: boolean | string;
};

const rowProps: any = { flexWrap: "wrap", gap: "16", my: "4" };
const inputProps = { minWidth: 200, flex: 1 };

const genderOptions: Option[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
];

const toId = (v: any): number | null => {
  if (typeof v === "number") return v;
  if (v && typeof v === "object" && typeof v.id === "number") return v.id;
  return null;
};

export const ProfileMonitoringDetailsStep = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const { profile } = useOutletContext<any>();
  const isEntity = profile?._type === "entity";

  const { data: countries, isLoading: isLoadingCountries } =
    useCountries() as any;
  const { data: entityTypes, isLoading: isLoadingEntityTypes } =
    useEntityTypes() as any;
  const { data: titlesData, isLoading: isLoadingTitles } =
    useCommonTitles() as any;

  const { options: companyStatusOptions, isLoading: isLoadingCompanyStatuses } =
    useCompanyStatusOptions();

  const { mutate: patchProfile } = usePatchProfile();
  const { mutate: createComment } = useCreateComment();

  const {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
  } = useMembershipDivisionTeam();

  const [doubleClick, setDoubleClick] = useState(false);

  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);
  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
  const detailsFormikRef = useRef<any>(null);
  const isLoadingLookups =
    isLoadingCountries ||
    isLoadingEntityTypes ||
    isLoadingTitles ||
    isLoadingCompanyStatuses;

  const countryOptions: Option[] = useMemo(() => {
    const list = Array.isArray(countries) ? countries : countries?.data ?? [];
    return list.map((c: any) => ({ label: c.name, value: c.id }));
  }, [countries]);

  const entityTypeOptions: Option[] = useMemo(() => {
    const list = Array.isArray(entityTypes)
      ? entityTypes
      : entityTypes?.data ?? [];
    return list.map((t: any) => ({ label: t.name, value: t.id }));
  }, [entityTypes]);

  const titleOptions: Option[] = useMemo(() => {
    return (titlesData?.data ?? []).map(({ name }: any) => ({
      label: name,
      value: name,
    }));
  }, [titlesData]);

  const initialDetailsValues: DetailsValues = useMemo(
    () => ({
      externalReference: profile?.external_reference ?? "",
      division: singleDivisionId,
      team: singleTeamId,

      // entity
      name: profile?.name ?? "",
      country: toId(profile?.country),
      entity_type: toId(profile?.entity_type),
      company_status:
        toId(profile?.company_status) ?? toId(profile?.status) ?? null,
      registration_number: profile?.registration_number ?? "",
      date_of_incorporation: toJSDate(profile?.date_of_incorporation),

      // individual
      title: profile?.title ?? null,
      firstName: profile?.first_name ?? "",
      middleName: profile?.middle_name ?? "",
      lastName: profile?.last_name ?? "",
      dateOfBirth: toJSDate(profile?.date_of_birth),
      gender: profile?.gender ?? "",
    }),
    [profile, singleDivisionId, singleTeamId],
  );

  const initialValues: FormValues = useMemo(
    () => ({
      ...initialDetailsValues,
      pep_and_sanction_required: "",
      adverse_media_required: "",
    }),
    [initialDetailsValues],
  );

  const entityValidation = useMemo(() => {
    const Yup = require("yup");

    const nullableNumber = () =>
      Yup.number()
        .nullable()
        .transform((value: any, originalValue: any) =>
          originalValue === "" || originalValue === undefined ? null : value,
        );

    return Yup.object().shape({
      division: Yup.string().trim().required("The division field is required."),
      team: Yup.string().trim().required("The team field is required."),
      name: noHtmlTags(Yup.string().trim()).required("Entity name is required"),
      country: nullableNumber(),
      entity_type: nullableNumber().required("Entity type is required"),
      company_status: nullableNumber(),
      registration_number: noHtmlTags(Yup.string().trim()).nullable(),
      externalReference: noHtmlTags(Yup.string().trim()).nullable(),
      date_of_incorporation: Yup.mixed().nullable(),
    });
  }, []);

  const individualValidation = useMemo(() => {
    const Yup = require("yup");

    return addIndividualValidationOptionalEmail.shape({
      externalReference: noHtmlTags(Yup.string().trim()).nullable(),
    });
  }, []);

  const validationSchema = useMemo(
    () =>
      (isEntity ? entityValidation : individualValidation).concat(
        monitoringEventsValidation,
      ),
    [isEntity, entityValidation, individualValidation],
  );

  const comparableSubset = (values: DetailsValues) => {
    if (isEntity) {
      return {
        name: values.name,
        country: values.country,
        entity_type: values.entity_type,
        company_status: values.company_status,
        registration_number: values.registration_number,
        date_of_incorporation: values.date_of_incorporation,
        externalReference: values.externalReference,
      };
    }
    return {
      title: values.title,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
      gender: values.gender,
      externalReference: values.externalReference,
    };
  };

  const goToSummary = (values: FormValues) => {
    navigate(`/profile/${profileId}/monitoring/new/summary`, {
      state: {
        monitoring: {
          pep_and_sanction_required: values.pep_and_sanction_required,
          adverse_media_required: values.adverse_media_required,
        },
        externalReference: values.externalReference,
        primaryDivision: values.division,
        primaryTeam: values.team,
      },
    });
  };

  const handleNext = (values: FormValues) => {
    if (doubleClick) return;

    const changed = hasChanges(
      comparableSubset(values),
      comparableSubset(initialDetailsValues),
    );

    if (changed) {
      setPendingValues(values);
      setIsChangeModalOpen(true);
      return;
    }

    setDoubleClick(true);
    goToSummary(values);
  };

  const handleSkip = () => {
    setIsChangeModalOpen(false);

    if (pendingValues) {
      setDoubleClick(true);
      goToSummary(pendingValues);
    }

    setPendingValues(null);
  };

  const handleUpdate = (comment: string) => {
    if (!pendingValues) {
      setIsChangeModalOpen(false);
      return;
    }

    if (!comment?.trim()) {
      showError(null as any, "Please enter a comment before updating.");
      return;
    }

    const patchPayload = isEntity
      ? {
          _type: "entity" as const,
          name: pendingValues.name,
          country: pendingValues.country,
          entity_type: pendingValues.entity_type,
          company_status: pendingValues.company_status || null,
          registration_number: pendingValues.registration_number || null,
          date_of_incorporation: pendingValues.date_of_incorporation
            ? DateTime.fromJSDate(
                pendingValues.date_of_incorporation,
              ).toISODate()
            : null,
          external_reference: pendingValues.externalReference?.trim() || null,
        }
      : {
          _type: "individual" as const,
          title: pendingValues.title === "" ? null : pendingValues.title,
          gender: pendingValues.gender || null,
          first_name: pendingValues.firstName,
          middle_name: pendingValues.middleName || null,
          last_name: pendingValues.lastName,
          date_of_birth: pendingValues.dateOfBirth
            ? DateTime.fromJSDate(pendingValues.dateOfBirth as Date).toISODate()
            : null,
          external_reference: pendingValues.externalReference?.trim() || null,
        };

    patchProfile({ profileId, ...patchPayload } as any, {
      onSuccess: () => {
        notify("Profile details updated successfully!", {
          position: "bottom-left",
          type: "success",
        });

        createComment(
          {
            comment: comment.trim(),
            comment_for: "Profile",
            reference: profileId,
          } as any,
          {
            onSuccess: () => {
              setIsChangeModalOpen(false);
              setDoubleClick(true);
              goToSummary(pendingValues);
              setPendingValues(null);
            },
            onError: (err: any) => {
              showError(err, "Profile updated but failed to add comment.");
              setIsChangeModalOpen(false);
              setPendingValues(null);
            },
          },
        );
      },
      onError: (err: any) => {
        showError(err, "Failed to update Profile.");
        setIsChangeModalOpen(false);
        setPendingValues(null);
      },
    });
  };

  return (
    <NewMonitoringRecordFrame step="monitoringDetails">
      <Box mt={2} maxWidth={760} minWidth={650}>
        <H2>Monitoring Details</H2>

        <Box mt={3} mb={3}>
          <Paragraph fontWeight={700} mb={2}>
            {isEntity ? "Entity Details" : "Individual Details"}
          </Paragraph>

          {isLoadingLookups && (
            <Flex justifyContent="center" alignItems="center" minHeight="15vh">
              <Loader />
            </Flex>
          )}

          {!isLoadingLookups && (
            <Formik
              innerRef={detailsFormikRef}
              initialValues={initialValues}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={handleNext}
            >
              {(props) => (
                <Form onSubmit={props.handleSubmit}>
                  <Flex {...rowProps}>
                    <Input
                      {...inputProps}
                      name="externalReference"
                      label="Your reference"
                      placeholder="Your Reference"
                      mt="2"
                      value={props.values.externalReference}
                      onChange={props.handleChange}
                      hasError={Boolean(
                        (props.errors as any).externalReference,
                      )}
                      errorMessage={
                        (props.errors as any).externalReference as string
                      }
                    />
                  </Flex>

                  {showDivisionField && (
                    <Flex {...rowProps}>
                      <Select
                        {...inputProps}
                        onChange={(option: any) => {
                          const newDivisionTeams = teamOptionsFor(
                            option.value,
                          );
                          props.setValues({
                            ...props.values,
                            division: option.value,
                            team:
                              newDivisionTeams.length === 1
                                ? newDivisionTeams[0].value
                                : "",
                          });
                        }}
                        value={divisionOptions?.find(
                          ({ value }: any) => value === props.values.division,
                        )}
                        options={divisionOptions}
                        placeholder="Choose division"
                        label="Division"
                        name="division"
                        isRequired
                        hasError={Boolean((props.errors as any).division)}
                        errorMessage={(props.errors as any).division as string}
                      />
                    </Flex>
                  )}
                  {teamOptionsFor(props.values.division).length > 0 && (
                    <Flex {...rowProps}>
                      <Select
                        {...inputProps}
                        key={props.values.division}
                        onChange={(option: any) =>
                          props.setFieldValue("team", option.value)
                        }
                        value={
                          teamOptionsFor(props.values.division).find(
                            ({ value }: any) => value === props.values.team,
                          ) ?? null
                        }
                        options={teamOptionsFor(props.values.division)}
                        placeholder="Choose team"
                        label="Team"
                        name="team"
                        isRequired
                        hasError={Boolean((props.errors as any).team)}
                        errorMessage={(props.errors as any).team as string}
                      />
                    </Flex>
                  )}

                  {isEntity ? (
                    <>
                      <Flex {...rowProps}>
                        <Input
                          {...inputProps}
                          name="name"
                          label="Entity name"
                          placeholder="Enter entity name"
                          mt="2"
                          value={props.values.name}
                          onChange={props.handleChange}
                          hasError={Boolean((props.errors as any).name)}
                          errorMessage={(props.errors as any).name as string}
                          isRequired
                        />
                      </Flex>

                      <Flex {...rowProps}>
                        <div style={{ width: "100%" }}>
                          <Select
                            name="country"
                            label="Country of registration/establishment"
                            placeholder="Select country"
                            mt="2"
                            flex={1}
                            options={countryOptions}
                            value={countryOptions.find(
                              (o) => o.value === props.values.country,
                            )}
                            onChange={(option: any) =>
                              props.setFieldValue(
                                "country",
                                option?.value ?? null,
                              )
                            }
                          />
                        </div>
                      </Flex>

                      <Flex {...rowProps}>
                        <div style={{ width: "48%" }}>
                          <Select
                            name="entity_type"
                            label="Entity type"
                            placeholder="Select entity type"
                            mt="2"
                            isRequired
                            flex={1}
                            options={entityTypeOptions}
                            value={entityTypeOptions.find(
                              (o) => o.value === props.values.entity_type,
                            )}
                            onChange={(option: any) =>
                              props.setFieldValue(
                                "entity_type",
                                option?.value ?? null,
                              )
                            }
                          />
                        </div>

                        <div style={{ width: "48%" }}>
                          <Select
                            name="company_status"
                            label="Current status"
                            placeholder="Select current status"
                            mt="2"
                            flex={1}
                            options={companyStatusOptions}
                            value={companyStatusOptions.find(
                              (o) => o.value === props.values.company_status,
                            )}
                            onChange={(option: any) =>
                              props.setFieldValue(
                                "company_status",
                                option?.value ?? null,
                              )
                            }
                          />
                        </div>
                      </Flex>

                      <Flex {...rowProps}>
                        <Input
                          {...inputProps}
                          name="registration_number"
                          label="Registration number"
                          placeholder="Enter registration number"
                          mt="2"
                          value={props.values.registration_number}
                          onChange={props.handleChange}
                        />

                        <DatePicker
                          {...inputProps}
                          name="date_of_incorporation"
                          label="Date of registration/establishment"
                          mt="2"
                          value={props.values.date_of_incorporation as any}
                          onChange={(date: any) =>
                            props.setFieldValue("date_of_incorporation", date)
                          }
                        />
                      </Flex>
                    </>
                  ) : (
                    <>
                      <Flex {...rowProps}>
                        <div style={{ width: "48%" }}>
                          <Select
                            name="title"
                            label="Title"
                            placeholder="Select title"
                            mt="2"
                            flex={1}
                            options={titleOptions}
                            isRequired
                            value={titleOptions.find(
                              (o) => o.value === props.values.title,
                            )}
                            onChange={(option: any) =>
                              props.setFieldValue(
                                "title",
                                option?.value ?? null,
                              )
                            }
                            hasError={Boolean((props.errors as any).title)}
                            errorMessage={(props.errors as any).title as string}
                          />
                        </div>

                        <div style={{ width: "48%" }}>
                          <Select
                            name="gender"
                            label="Select gender"
                            placeholder="Select gender"
                            mt="2"
                            isRequired
                            flex={1}
                            options={genderOptions}
                            value={genderOptions.find(
                              (o) => o.value === props.values.gender,
                            )}
                            onChange={(option: any) =>
                              props.setFieldValue("gender", option?.value ?? "")
                            }
                            hasError={Boolean((props.errors as any).gender)}
                            errorMessage={
                              (props.errors as any).gender as string
                            }
                          />
                        </div>
                      </Flex>

                      <Flex {...rowProps}>
                        <Input
                          {...inputProps}
                          name="firstName"
                          label="First name"
                          placeholder="Enter first name"
                          mt="2"
                          value={props.values.firstName}
                          onChange={props.handleChange}
                          hasError={Boolean((props.errors as any).firstName)}
                          errorMessage={
                            (props.errors as any).firstName as string
                          }
                          isRequired
                        />
                        <Input
                          {...inputProps}
                          name="middleName"
                          label="Middle name(s)"
                          placeholder="Enter middle(s) name"
                          mt="2"
                          value={props.values.middleName}
                          onChange={props.handleChange}
                          hasError={Boolean((props.errors as any).middleName)}
                          errorMessage={
                            (props.errors as any).middleName as string
                          }
                        />
                      </Flex>

                      <Flex {...rowProps}>
                        <Input
                          {...inputProps}
                          name="lastName"
                          label="Last name"
                          placeholder="Enter last name"
                          mt="2"
                          value={props.values.lastName}
                          onChange={props.handleChange}
                          hasError={Boolean((props.errors as any).lastName)}
                          errorMessage={
                            (props.errors as any).lastName as string
                          }
                          isRequired
                        />
                        <DatePicker
                          {...inputProps}
                          name="dateOfBirth"
                          label="Date of birth"
                          mt="2"
                          value={props.values.dateOfBirth as any}
                          onChange={(date: any) =>
                            props.setFieldValue("dateOfBirth", date)
                          }
                          maxDate={DateTime.now()
                            .minus({ years: MIN_AGE_APPLICATION })
                            .toJSDate()}
                          hasError={Boolean((props.errors as any).dateOfBirth)}
                          errorMessage={
                            (props.errors as any).dateOfBirth as string
                          }
                        />
                      </Flex>
                    </>
                  )}

                  <Box
                    borderTop={`1px solid ${theme.colors.divider}`}
                    pt={16}
                    mt={16}
                  >
                    <MonitoringEventsFields
                      formikProps={props}
                      rowProps={{ mb: 3 }}
                      pepLabel={isEntity ? "Sanctions" : "PEP & sanctions"}
                      adverseLabel={isEntity ? "Entity Adverse Media" : "Adverse Media"}
                      intro={
                        <Paragraph fontWeight={700} mb={2}>
                          Monitoring Events
                        </Paragraph>
                      }
                    />

                    <Flex justifyContent="space-between" mt={3}>
                      <Button
                        variant="text"
                        type="button"
                        onClick={() => navigate(`/profile/${profileId}`)}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="submit"
                        isDisabled={!props.isValid || doubleClick}
                      >
                        Next
                      </Button>
                    </Flex>
                  </Box>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </Box>

      <Modal
        isOpen={isChangeModalOpen}
        onClose={() => setIsChangeModalOpen(false)}
        title="Update profile?"
        content="Would you update the profile record with the changes you have made. If so please enter a comment before updating."
        hasSecondaryActionButton
        secondaryButtonText="Skip"
        secondaryButtonVariant="text"
        onSecondaryButtonClick={handleSkip}
        width="520px"
        primaryButtonText="Update"
        primaryButtonVariant="primary"
        onModalConfirmClick={handleUpdate}
        hasCommentBox
        loadingActionText="Updating..."
      />
    </NewMonitoringRecordFrame>
  );
};
